import { useState } from 'react'
import { useAbruf } from '../api'
import { Angabe, Karte, Merkmal, Tabelle, Zustand } from '../bausteine'
import { datum, geld, prozent, zahl } from '../formate'

interface Produktzeile {
  id: string
  produktnummer: string
  bezeichnung: string
  art: string
  kundenart: string
  oekostrom: boolean
  guetesiegel: string | null
  mindestlaufzeitMonate: number | null
  preisgarantieMonate: number | null
  vertriebAb: string | null
  vertriebBis: string | null
}

interface Preisauskunft {
  stichtag: string
  arbeitspreisNettoCt: string
  arbeitspreisBruttoCt: string
  grundpreisNettoEurJahr: string
  grundpreisBruttoEurJahr: string
  umsatzsteuersatz: string
  arbeitspreisAnteileCt: Record<string, string>
  anteileProzent: Record<string, string>
  hinweise: string[]
}

export default function Produkte() {
  const [kundenart, setzeKundenart] = useState('PRIVATKUNDE')
  const produkte = useAbruf<Produktzeile[]>(`/api/produkte?kundenart=${kundenart}`)
  const [gewaehlt, setzeGewaehlt] = useState<Produktzeile>()
  const preise = useAbruf<Preisauskunft>(
    gewaehlt ? `/api/produkte/${gewaehlt.id}/preisauskunft` : undefined,
    [gewaehlt?.id],
  )

  return (
    <>
      <h1>Produkte und Preise</h1>
      <p className="untertitel">
        Der Preis wird nach Paragraf 40 EnWG aufgeschlüsselt: Energie und Vertrieb,
        Netzentgelt, Messstellenbetrieb sowie Steuern, Abgaben und Umlagen.
      </p>

      <div className="leiste">
        <div className="feld">
          <label htmlFor="kundenart">Kundenart</label>
          <select
            id="kundenart"
            value={kundenart}
            onChange={(ereignis) => {
              setzeGewaehlt(undefined)
              setzeKundenart(ereignis.target.value)
            }}
          >
            <option value="PRIVATKUNDE">Privatkunde</option>
            <option value="GEWERBEKUNDE">Gewerbekunde</option>
          </select>
        </div>
      </div>

      <Karte>
        <Zustand
          laeuft={produkte.laeuft}
          fehler={produkte.fehler}
          leer={(produkte.daten ?? []).length === 0}
          leertext="Kein vertriebsfähiges Produkt."
        >
          <Tabelle
            spalten={[
              { name: 'Produktnummer' },
              { name: 'Bezeichnung' },
              { name: 'Art' },
              { name: 'Laufzeit' },
              { name: 'Preisgarantie' },
              { name: 'Vertrieb' },
              { name: '' },
            ]}
          >
            {(produkte.daten ?? []).map((produkt) => (
              <tr key={produkt.id}>
                <td>{produkt.produktnummer}</td>
                <td>
                  {produkt.bezeichnung}
                  {produkt.oekostrom && (
                    <>
                      {' '}
                      <Merkmal art="gut">Ökostrom</Merkmal>
                    </>
                  )}
                  {produkt.guetesiegel && (
                    <div style={{ fontSize: 12, color: 'var(--gedaempft)' }}>
                      {produkt.guetesiegel}
                    </div>
                  )}
                </td>
                <td>{produkt.art}</td>
                <td>
                  {produkt.mindestlaufzeitMonate
                    ? `${produkt.mindestlaufzeitMonate} Monate`
                    : '—'}
                </td>
                <td>
                  {produkt.preisgarantieMonate ? `${produkt.preisgarantieMonate} Monate` : '—'}
                </td>
                <td>
                  {datum(produkt.vertriebAb)} – {datum(produkt.vertriebBis)}
                </td>
                <td>
                  <button onClick={() => setzeGewaehlt(produkt)}>Preis</button>
                </td>
              </tr>
            ))}
          </Tabelle>
        </Zustand>
      </Karte>

      {gewaehlt && (
        <Karte titel={`Preisauskunft: ${gewaehlt.bezeichnung}`}>
          <Zustand laeuft={preise.laeuft} fehler={preise.fehler}>
            {preise.daten && (
              <>
                {preise.daten.hinweise.length > 0 && (
                  <div className="hinweis warnung">
                    <strong>Der Preis ließ sich nicht vollständig herleiten:</strong>
                    <ul>
                      {preise.daten.hinweise.map((hinweis) => (
                        <li key={hinweis}>{hinweis}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="angaben" style={{ marginBottom: 16 }}>
                  <Angabe beschriftung="Stichtag">{datum(preise.daten.stichtag)}</Angabe>
                  <Angabe beschriftung="Arbeitspreis netto">
                    {zahl(preise.daten.arbeitspreisNettoCt, 4)} ct/kWh
                  </Angabe>
                  <Angabe beschriftung="Arbeitspreis brutto">
                    {zahl(preise.daten.arbeitspreisBruttoCt, 4)} ct/kWh
                  </Angabe>
                  <Angabe beschriftung="Grundpreis brutto">
                    {geld(preise.daten.grundpreisBruttoEurJahr)} je Jahr
                  </Angabe>
                  <Angabe beschriftung="Umsatzsteuer">
                    {prozent(preise.daten.umsatzsteuersatz)}
                  </Angabe>
                </div>

                <Tabelle
                  spalten={[
                    { name: 'Bestandteil' },
                    { name: 'ct/kWh', zahl: true },
                    { name: 'Anteil', zahl: true },
                  ]}
                >
                  {Object.entries(preise.daten.arbeitspreisAnteileCt).map(
                    ([bestandteil, wert]) => (
                      <tr key={bestandteil}>
                        <td>{bestandteil}</td>
                        <td className="zahl">{zahl(wert, 4)}</td>
                        <td className="zahl">
                          {prozent(preise.daten!.anteileProzent[bestandteil])}
                        </td>
                      </tr>
                    ),
                  )}
                </Tabelle>
              </>
            )}
          </Zustand>
        </Karte>
      )}
    </>
  )
}
